/** Подсветка таблицы ФБС — ЧИСТЫЕ правила, без React и без API.
 *
 *  Подсвечиваем ровно то, что требует вмешательства, и ровно тремя правилами (владелец,
 *  21.09.2026):
 *  - остаток на складе ФБС кончился (0 или минус), а продажи идут — красным (площадка уже теряет
 *    заказы); минус красный и без продаж: у пула комнаты wh-helper он значит «несобранных заказов
 *    больше, чем лежит» (15.10.2026) — это уже долг, а не «позицию не возят»;
 *  - глубины не хватает до горизонта пополнения — красным (не дотянет до следующего завоза);
 *  - «К перемещению» срезано свободным остатком ГП на складе-источнике — оранжевым (везти нечего,
 *    число меньше потребности не потому, что столько нужно).
 *
 *  Пороги берутся из ПАРАМЕТРОВ РАСЧЁТА отчёта (`report.params`), а не из констант страницы:
 *  горизонт — настройка пользователя, и зашитое число разъехалось бы с тем, чем считали.
 *  Горизонт не число или не положительный — исключение, а не «подсветим по 7 дням»: подсветка,
 *  посчитанная не по тем числам, хуже отсутствующей.
 *
 *  Классы Tailwind живут здесь же — легенда над таблицей и ячейки обязаны красить одним и тем же,
 *  а две копии строки классов уже начали бы расходиться.
 */

/** Флаг строки расчёта: рекомендацию срезал свободный остаток склада-источника (`fbs_calc`). */
export const FLAG_SOURCE_LIMITED = "source_limited";

/** «Требует внимания»: пустой склад, недостаточная глубина. */
export const DANGER_CLASS = "font-medium text-red-600 dark:text-red-400";

/** «Везти нечего»: число срезано остатком ГП. */
export const CUT_CLASS = "font-medium text-orange-600 dark:text-orange-400";

/** Строка расчёта в том объёме, который нужен правилам (полный вид — `api.FbsReportRow`). */
export interface FbsHighlightRow {
  orders_qty: number;
  stock_qty: number;
  /** null = продаж за окно не было, глубина не определена. */
  depth_days: number | null;
  flags: readonly string[];
}

/** Пороги подсветки = параметры, которыми считали отчёт (`api.FbsParams`). */
export interface FbsHighlightParams {
  horizon_days: number;
}

function horizonOf(params: FbsHighlightParams): number {
  const h = params.horizon_days;
  if (typeof h !== "number" || !Number.isFinite(h) || h <= 0)
    throw new Error(`Горизонт расчёта не задан числом (${String(h)}) — подсветку считать нечем`);
  return h;
}

/** Склад ФБС пуст, а продажи идут. Отрицательный остаток — тем более пусто. */
export function stockAlarm(row: FbsHighlightRow): boolean {
  return row.orders_qty > 0 && row.stock_qty <= 0;
}

/** Остаток ФБС меньше нуля: обещано (несобранные заказы, собранное-не-проведённое) больше, чем
 *  лежит. Тревога независимо от продаж за окно. */
export function stockNegative(row: Pick<FbsHighlightRow, "stock_qty">): boolean {
  return row.stock_qty < 0;
}

/** Запаса не хватает до горизонта пополнения. Продаж нет → глубины нет → и тревоги нет. */
export function depthAlarm(row: FbsHighlightRow, params: FbsHighlightParams): boolean {
  const horizon = horizonOf(params);
  return row.depth_days !== null && row.depth_days < horizon;
}

/** «К перемещению» срезано свободным остатком ГП склада-источника. */
export function cutBySource(row: FbsHighlightRow): boolean {
  return row.flags.includes(FLAG_SOURCE_LIMITED);
}

/** Класс ячейки «Остаток ФБС» («» = без подсветки). */
export function stockClass(row: FbsHighlightRow): string {
  return stockAlarm(row) || stockNegative(row) ? DANGER_CLASS : "";
}

/** Класс ячейки «Глубина, дн». */
export function depthClass(row: FbsHighlightRow, params: FbsHighlightParams): string {
  return depthAlarm(row, params) ? DANGER_CLASS : "";
}

/** Класс значения «К перемещению». */
export function qtyClass(row: FbsHighlightRow): string {
  return cutBySource(row) ? CUT_CLASS : "";
}
